import { useEffect, useMemo } from 'react';
import { Create, useForm } from '@refinedev/antd';
import { useList, HttpError } from '@refinedev/core';
import { Form, Input, Select, Alert } from 'antd';
import type { Company } from '@viq-admin/types';
import { normalizeEmailInput, space } from '@viq-admin/ui';
import { Conference, Role, UserProfile } from '../../api/types';
import { useRoleClaim } from '../../auth/useRoleClaim';
import { ROLE_OPTIONS } from '../../labels/display';

interface InviteUserInput {
  name: string;
  email: string;
  companyId: string;
  role: Role;
  conferenceIds: string[];
}

/**
 * Invite form. company_admin only: companyId is locked to the caller's
 * own company; role is locked to field_rep; conferences are filtered to
 * those the caller's company has access to.
 *
 * Server enforces all of this — the client locks the fields for UX
 * only.
 *
 * The locked Company field must show the caller's company NAME, not the raw
 * `callerCompanyId` UID from the Entra claim (`useRoleClaim` has no name).
 * A disabled AntD Select with a value but no matching `options` entry falls
 * back to rendering the raw value, which is how this used to leak the UID.
 * Fetching `companies` here and matching on id gives the Select a real
 * `{ label, value }` option so it renders the name instead. This `companies`
 * resource is mapped in the dataProvider (GET /v1/admin/companies) but has
 * no other caller today, so it's unverified on this plane — if the fetch
 * fails or returns nothing, `companyName` stays undefined and the option
 * label falls back to the raw UID, i.e. no worse than before.
 */
export function UserCreate() {
  const { role: callerRole, companyId: callerCompanyId } = useRoleClaim();
  const { formProps, saveButtonProps, form } = useForm<
    UserProfile,
    HttpError,
    InviteUserInput
  >({ resource: 'users', action: 'create' });

  const companyFieldEnabled =
    callerRole === 'company_admin' && !!callerCompanyId;

  const { result: conferencesResult } = useList<Conference>({
    resource: 'conferences',
    queryOptions: {
      enabled: callerRole === 'company_admin',
    },
  });
  const { result: companiesResult } = useList<Company>({
    resource: 'companies',
    queryOptions: {
      enabled: companyFieldEnabled,
    },
  });

  const conferenceOptions = useMemo(
    () =>
      (conferencesResult.data ?? []).map((c: Conference) => ({
        label: `${c.name}${c.location ? ` — ${c.location}` : ''}`,
        value: c.id,
      })),
    [conferencesResult.data],
  );

  const companyName = (companiesResult.data ?? []).find(
    (c) => c.id === callerCompanyId,
  )?.name;
  const companyOptions = useMemo(
    () =>
      callerCompanyId
        ? [{ label: companyName ?? callerCompanyId, value: callerCompanyId }]
        : [],
    [callerCompanyId, companyName],
  );

  useEffect(() => {
    if (callerRole === 'company_admin' && callerCompanyId) {
      form.setFieldsValue({ companyId: callerCompanyId, role: 'field_rep' });
    }
  }, [callerRole, callerCompanyId, form]);

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: space.lg }}
        message="Your role lets you invite field reps into your own company only. Role and company are locked."
      />
      <Form {...formProps} layout="vertical">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          normalize={normalizeEmailInput}
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input type="email" maxLength={200} />
        </Form.Item>
        <Form.Item
          label="Company"
          name="companyId"
          rules={[{ required: true, message: 'Company is required' }]}
        >
          <Select
            disabled
            placeholder="Locked to your company"
            options={companyOptions}
          />
        </Form.Item>
        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Role is required' }]}
        >
          <Select
            disabled
            options={ROLE_OPTIONS.filter((o) => o.value === 'field_rep')}
          />
        </Form.Item>
        <Form.Item label="Conferences" name="conferenceIds">
          <Select
            mode="multiple"
            placeholder="Optional: assign to conferences immediately"
            options={conferenceOptions}
            allowClear
          />
        </Form.Item>
      </Form>
    </Create>
  );
}
